import React from 'react';
import { Button } from '@mui/material';
import { Payment as PaymentIcon } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../../../app/store';
import { useUpdateRefundMutation } from '../../../../services/RefundService';
import { clearBackupAction as clearBackup } from '../../../../services/RefundService/slice';
import { useNavigate } from 'react-router-dom';

interface PaidButtonProps {
  onError?: (message: string) => void;
}

export const PaidButton: React.FC<PaidButtonProps> = ({ onError }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentRefund } = useAppSelector(state => state.refund);
  const [updateRefundMutation, { isLoading }] = useUpdateRefundMutation();

  const status = currentRefund?.status;
  const isVisible = status === 'AP' || status === 'POSTING' || status === 'CREDIT_CARD';

  if (!isVisible) return null;

  const handlePaid = async () => {
    if (!currentRefund) return;

    let newStatus = 'COMPLETE';

    // Determine the target status based on current status
    if (currentRefund.status === 'AP') {
      // Validate check number and check date are filled in for AP status
      const checkInfo = currentRefund.checkInfo;
      if (!checkInfo.checkNumber || !checkInfo.checkDate) {
        onError?.('Check number and check date are required before marking as paid.');
        return;
      }
      newStatus = 'POSTING';
    } else if (currentRefund.status === 'CREDIT_CARD') {
      // Credit card refunds can go directly to posting without check validation
      newStatus = 'POSTING';
    } else if (currentRefund.status === 'POSTING') {
      newStatus = 'COMPLETE';
    }

    // Create a copy with the new status
    const refundForPaid = {
      ...currentRefund,
      status: newStatus
    };

    try {
      await updateRefundMutation({ refundView: refundForPaid }).unwrap();
      dispatch(clearBackup());
      navigate(-1);
    } catch (err) {
      onError?.('Failed to update refund status. Please try again.');
    }
  };

  const getButtonText = () => {
    if (status === 'AP') return isLoading ? 'Processing...' : 'Paid';
    if (status === 'POSTING') return isLoading ? 'Posting...' : 'Posted';
    return isLoading ? 'Processing...' : 'Paid';
  };

  return (
    <Button
      variant="contained"
      startIcon={<PaymentIcon />}
      onClick={handlePaid}
      disabled={isLoading}
      sx={{
        backgroundColor: '#059669',
        '&:hover': {
          backgroundColor: '#047857',
        },
        fontWeight: 600
      }}
    >
      {getButtonText()}
    </Button>
  );
};
